import React, { useEffect, useMemo, useRef, useState } from "react"
import countries from "./data.json"

type CountrySelectProps = {
  value: string | null
  onChange: (next: string | null) => void

  label?: string
  placeholder?: string
  searchPlaceholder?: string

  disabled?: boolean
  className?: string

  searchInputProps?: React.InputHTMLAttributes<HTMLInputElement>
}

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
}

type Placement = "bottom" | "top"

export function CountrySelect({
  value,
  onChange,
  placeholder = "Select a country",
  searchPlaceholder = "Search…",
  disabled = false,
  className = "",
  searchInputProps,
}: CountrySelectProps) {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [activeIndex, setActiveIndex] = useState(0)
  const [placement] = useState<Placement>("top")

  const all = useMemo(
    () => countries.filter((x): x is string => typeof x === "string"),
    [],
  )

  const filtered = useMemo(() => {
    const q = normalize(query)
    if (!q) return all
    return all.filter((c) => normalize(c).includes(q))
  }, [all, query])

  useEffect(() => {
    if (activeIndex >= filtered.length)
      setActiveIndex(Math.max(0, filtered.length - 1))
  }, [filtered.length, activeIndex])

  const closeDropdown = () => {
    setOpen(false)
    setQuery("")
    requestAnimationFrame(() => buttonRef.current?.focus())
  }

  const commit = (label: string) => {
    onChange(label)
    closeDropdown()
  }

  useEffect(() => {
    if (!open) return
    requestAnimationFrame(() => {
      inputRef.current?.focus()
    })
  }, [open])

  useEffect(() => {
    if (!open) return

    const onMouseDown = (e: MouseEvent) => {
      const root = rootRef.current
      if (root && !root.contains(e.target as Node)) closeDropdown()
    }

    document.addEventListener("mousedown", onMouseDown)
    return () => document.removeEventListener("mousedown", onMouseDown)
  }, [open])

  const onInputKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Escape") {
      e.preventDefault()
      closeDropdown()
      return
    }
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1))
      return
    }
    if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
      return
    }
    if (e.key === "Enter") {
      e.preventDefault()
      const item = filtered[activeIndex]
      if (item) commit(item)
    }
  }

  useEffect(() => {
    if (!open) return
    const container = listRef.current
    const el = container?.querySelector<HTMLElement>(
      `[data-item-index="${activeIndex}"]`,
    )
    if (!container || !el) return

    const top = el.offsetTop
    const bottom = top + el.offsetHeight

    if (top < container.scrollTop) container.scrollTop = top
    else if (bottom > container.scrollTop + container.clientHeight) {
      container.scrollTop = bottom - container.clientHeight
    }
  }, [open, activeIndex])

  return (
    <div
      ref={rootRef}
      data-country-select-root
      className={`w-full ${className}`}
    >
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => {
            if (disabled) return
            setOpen((v) => !v)
            setActiveIndex(value ? Math.max(0, filtered.indexOf(value)) : 0)
          }}
          className={[
            "flex w-full items-center justify-between gap-2 px-3 h-[44px] py-3 text-left",
            "bg-white shadow-sm",
            disabled
              ? "cursor-not-allowed opacity-60"
              : "hover:bg-slate-50 focus:outline-none",
          ].join(" ")}
        >
          <span
            className={`min-w-0 flex-1 truncate ${value ? "" : "text-slate-400"}`}
          >
            {value ?? placeholder}
          </span>

          <span className="flex items-center gap-2">
            <svg
              className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 1 1 1.06 1.06l-4.24 4.24a.75.75 0 0 1-1.06 0L5.21 8.29a.75.75 0 0 1 .02-1.08Z"
                clipRule="evenodd"
              />
            </svg>
          </span>
        </button>

        {open ? (
          <div
            ref={panelRef}
            className={[
              "absolute z-50 w-full border bg-white shadow-lg",
              placement === "bottom" ? "mt-2 top-full" : "mb-2 bottom-full",
            ].join(" ")}
          >
            <div className="p-2">
              <input
                ref={inputRef}
                type="text"
                value={query}
                placeholder={searchPlaceholder}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setActiveIndex(0)
                }}
                onKeyDown={onInputKeyDown}
                {...searchInputProps}
                className={[
                  "w-full border-b px-3 py-2 text-base focus:outline-none placeholder-gray-200",
                  searchInputProps?.className ?? "",
                ].join(" ")}
              />
            </div>

            <div
              ref={listRef}
              role="listbox"
              className="max-h-64 overflow-auto p-2 pt-0"
            >
              {filtered.length === 0 ? (
                <div className=" px-3 py-2 text-sm text-slate-500">
                  No results
                </div>
              ) : (
                filtered.map((c, i) => {
                  const isActive = i === activeIndex
                  const isSelected = c === value

                  return (
                    <button
                      key={`${c}-${i}`}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      data-item-index={i}
                      onClick={() => commit(c)}
                      className={[
                        "flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-base",
                        isActive ? "bg-slate-900/5" : "hover:bg-slate-900/5",
                        isSelected
                          ? "font-medium text-slate-900"
                          : "text-slate-800",
                      ].join(" ")}
                    >
                      <span className="truncate">{c}</span>
                      {isSelected ? (
                        <svg
                          className="h-4 w-4 text-slate-700"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.01 7.06a1 1 0 0 1-1.423-.006L3.29 8.77a1 1 0 1 1 1.414-1.414l3.268 3.268 6.3-6.34a1 1 0 0 1 1.432.006Z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : null}
                    </button>
                  )
                })
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
